// src/components/LoginForm.tsx
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../context/AuthContext"; // Import the auth store
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

interface UserProfile {
  id: string;
  username: string;
  roles?: string[];
  // Add any other user data you want to store (e.g., company_id from Odoo)
  company_id?: number;
}

const LoginForm: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login); // Get the login action from store

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/auth/login`, {
        username,
        password,
      });

      const { access_token } = response.data;
      // In a real app, you might decode the token or make another API call
      // to get full user profile based on the token.
      // For simplicity, we'll extract basic info from the token or assume it's returned.
      // If your backend sends user data along with the token:

      // If backend only returns token, you might decode it (less secure client-side)
      // Or make a /auth/profile call after login to get user details.
      // For now, let's assume backend sends basic user info or we use username/id from token payload.
      // A safer approach: make a /auth/profile call after login to get verified user data.
      // For this example, let's assume the backend sends back user details in the login response
      // or we construct a basic profile from the username.
      // A more robust way:
      // const profileResponse = await axios.get(`${BACKEND_URL}/auth/profile`, {
      //   headers: { Authorization: `Bearer ${access_token}` }
      // });
      // const userProfile = profileResponse.data;

      // For now, let's use a simple user profile based on username and a dummy ID
      // You should adjust this based on what your backend actually returns or what's in your JWT payload.
      const decodedTokenPayload = JSON.parse(atob(access_token.split(".")[1]));
      const userForStore: UserProfile = {
        id: decodedTokenPayload.sub, // 'sub' is typically the user ID in JWT
        username: decodedTokenPayload.username,
        roles: decodedTokenPayload.roles || ["user"],
        company_id: decodedTokenPayload.company_id, // If your JWT includes company_id
      };

      login(access_token, userForStore); // Store token and user info
      navigate("/"); // Redirect to dashboard or home page
    } catch (err: any) {
      console.error("Login failed:", err);
      setError(
        err.response?.data?.message ||
          "Login failed. Please check your credentials.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          Login
        </h2>
        <form onSubmit={handleSubmit}>
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              {error}
            </div>
          )}
          <div className="mb-4">
            <label
              htmlFor="username"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Username:
            </label>
            <input
              type="text"
              id="username"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div className="mb-6">
            <label
              htmlFor="password"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Password:
            </label>
            <div className="relative mb-3">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pr-16"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <div className="absolute right-0 inset-y-0 pr-2 z-10 inline-flex items-center">
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="p-2 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  {showPassword ? (
                    <EyeIcon className="size-4" />
                  ) : (
                    <EyeSlashIcon className="size-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
